/** Shared presentation rules for report-quality verdicts. */

import type { Bucket } from './api';

/** Badge styling per verdict. Green/amber/red rather than the brand teal: this is a judgement
 *  about quality, and the teal is the product's own accent — it would read as decoration. */
export const BUCKET_STYLES: Record<Bucket, string> = {
  high: 'bg-green-50 text-green-700 border-green-200',
  mid: 'bg-amber-50 text-amber-800 border-amber-200',
  low: 'bg-red-50 text-red-700 border-red-200',
};

export const FAILED_STYLE = 'bg-gray-100 text-gray-600 border-gray-200';
export const UNCLASSIFIED_STYLE = 'bg-white text-gray-400 border-gray-200';

/** Above this, review priority is drawn as urgent rather than merely elevated. */
export const URGENT_PRIORITY = 0.5;

/** How long to wait after the last keystroke before searching. */
export const SEARCH_DEBOUNCE_MS = 300;

export const PAGE_SIZE = 50;

/** Both buttons wait for a NEW run to land; audits take 257-743s. Deadline matches the backend. */
export const RUN_POLL_INTERVAL_MS = 10_000;
export const RUN_POLL_DEADLINE_MS = 30 * 60 * 1000;

/** Grounding ladder: green traces to a consumer, red traces to nothing. */
export const RADIUS_STYLES: Record<string, string> = {
  '0': 'bg-green-500',
  '1': 'bg-blue-400',
  '2': 'bg-amber-400',
  '3': 'bg-red-500',
};

export const RADIUS_ORDER = ['0', '1', '2', '3'] as const;

export const asPercent = (value: number | null): string =>
  value == null ? '—' : `${Math.round(value * 100)}%`;

export const asCount = (value: number | null | undefined): string =>
  value == null ? '—' : value.toLocaleString('en-US');

export const asWhen = (value: string | null): string => {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? ''
    : date.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};
